import { MelodyLib, VocalLib, displayAccidentals, spellInKey } from '@tunasong/music-lib'
import type { Melody, Scale } from '@tunasong/schemas'
import type { ChartSource, MelodySource, PickerItem } from './picker-items.js'

/**
 * What a picker row becomes once chosen (Pitch Trainer Phase 2, plan M6a; Joni §3, §4, §7, §9): the notes
 * to score against, fitted to the singer's range; what the singer hears; the question screen's status
 * lines; and the name of each note for the feedback. Pure, so every string is tested without audio.
 */

/** Something to sing: a picker row, or a half of one (Joni §4, §8) */
export type Singable =
  | { kind: 'phrase'; source: MelodySource; phrase: MelodyLib.Phrase }
  | { kind: 'guide-tones' | 'outline'; source: ChartSource; part: VocalLib.ChordPart }

export interface PracticeTarget {
  singable: Singable
  /** "Verse · bars 9–12"; for chord-only material always "Guide tones for Verse" (Joni §9) */
  title: string
  /** Joni §9: chord-only material only, the one place "melody" appears, to deny it */
  subLabel?: string
  /** What the take is scored against */
  reference: number[]
  /** Per reference note: "B4 in bar 10", or "Note 3 (on Am)" for chord-only material, never a bar */
  notes: { label: string; name: string; chord?: string }[]
  /** What Hear it and the audition play: the same notes, moved and slowed as the reference is */
  audition: Melody
  /** Joni §3, line 1: the key change or octave move, or null when nothing moved */
  keyLine: string | null
  /** Joni §3, line 2 */
  slowedLine: string | null
  /** For the original-key verdict: `fitPhrase`'s key change, never its octaves (Bowie, #2491) */
  keyChange: number
  songKey?: string
  practiceKey?: string
  /** The key the notes are spelled in, for naming what was sung instead (Joni, M6a) */
  spellingKey?: Scale
}

export type PreparedTarget =
  | { kind: 'ready'; target: PracticeTarget }
  | { kind: 'refused'; singable: Singable; spanSemitones: number; rangeSemitones: number; halves: Singable[] }

const subLabels = {
  'guide-tones': 'One note per chord — not the song’s melody.',
  outline: 'Up and down each chord — not the song’s melody.',
} as const

const keyDisplayName = (key: Scale) => `${displayAccidentals(key.root)} ${key.type ?? 'major'}`

const octaveOf = (midi: number) => Math.floor(midi / 12) - 1

export const noteLabel = (name: string, midi: number) => `${displayAccidentals(name)}${octaveOf(midi)}`

/** Joni §3: Melody Trainer's shipped string for the same action */
const octaveLine = (octaves: number) => {
  if (octaves === 0) {
    return null
  }
  const count = Math.abs(octaves) === 1 ? 'an octave' : `${Math.abs(octaves)} octaves`
  return `Transposed ${octaves < 0 ? 'down' : 'up'} ${count} to fit your range.`
}

const moved = (melody: Melody, semitones: number, rate: number): Melody => ({
  ...melody,
  tempo: { ...melody.tempo, bpm: melody.tempo.bpm * rate },
  notes: melody.notes.map(note => ({ ...note, midi: note.midi + semitones })),
})

const barsLabel = (phrase: MelodyLib.Phrase) =>
  phrase.source.startBar === phrase.source.endBar
    ? `bar ${phrase.source.startBar}`
    : `bars ${phrase.source.startBar}–${phrase.source.endBar}`

const phraseTitle = (source: MelodySource, phrase: MelodyLib.Phrase) =>
  `${source.section} · ${source.melodyIndex ? `melody ${source.melodyIndex}, ` : ''}${barsLabel(phrase)}`

/** Where a split inside a bar falls: on a felt beat, or between beats */
export interface MidBarCut {
  bar: number
  /** 1-based felt beat (Bowie, M6a: a dotted quarter in 6/8); absent when the cut falls between beats */
  beat?: number
}

const beatEpsilon = 1e-6

/**
 * Where a half starting at `onset` (beats from the melody's start) begins, when that is inside a bar rather
 * than on a bar line (Joni, M6a): after a pickup, or at the middle event of a phrase with no bar line to cut
 * at. Undefined on a bar line.
 */
export const midBarCut = (meter: Melody['tempo']['meter'], onset: number): MidBarCut | undefined => {
  const perBar = MelodyLib.barBeats(meter)
  const bar = Math.floor(onset / perBar + beatEpsilon)
  const within = onset - bar * perBar
  if (within < beatEpsilon) {
    return undefined
  }
  const beats = within / MelodyLib.feltBeat(meter)
  const onBeat = Math.abs(beats - Math.round(beats)) < beatEpsilon
  return { bar: bar + 1, ...(onBeat ? { beat: Math.round(beats) + 1 } : {}) }
}

/** Joni, M6a: the split line, and the two halves' names, for a cut inside a bar */
export const midBarLabels = ({ bar, beat }: MidBarCut) =>
  beat === undefined
    ? {
        split: `Split it in two partway through bar ${bar}.`,
        halves: [`First half (into bar ${bar})`, `Second half (from partway through bar ${bar})`],
      }
    : {
        split: `Split it in two at beat ${beat} of bar ${bar}.`,
        halves: [`First half (to beat ${beat} of bar ${bar})`, `Second half (from beat ${beat} of bar ${bar})`],
      }

const keyOf = (source: MelodySource) => source.melody.key ?? source.key

const halvesOf = (source: MelodySource, phrase: MelodyLib.Phrase): Singable[] => {
  const key = keyOf(source)
  const halves = MelodyLib.splitPhrase(source.melody, phrase, key ? { key } : {})
  return halves ? halves.map(half => ({ kind: 'phrase', source, phrase: half })) : []
}

const preparePhrase = (
  source: MelodySource,
  phrase: MelodyLib.Phrase,
  range: VocalLib.VocalRange | null
): PreparedTarget => {
  const singable: Singable = { kind: 'phrase', source, phrase }
  const key = keyOf(source)
  const fit = VocalLib.fitPhrase(
    phrase.events.map(event => event.midi),
    { range, ...(key ? { key } : {}) }
  )
  if (fit.kind === 'refused') {
    const { spanSemitones, rangeSemitones } = fit
    return { kind: 'refused', singable, spanSemitones, rangeSemitones, halves: halvesOf(source, phrase) }
  }
  const semitones = fit.kind === 'octave' ? fit.octaves * 12 : fit.kind === 'key-change' ? fit.semitones : 0
  const to = fit.kind === 'key-change' ? fit.to : undefined
  const keyLine =
    fit.kind === 'octave'
      ? octaveLine(fit.octaves)
      : fit.kind === 'key-change' && fit.from && fit.to
        ? `Moved from ${keyDisplayName(fit.from)} to ${keyDisplayName(fit.to)} to fit your range.`
        : null
  const notes = phrase.events.map((event, index) => {
    const midi = fit.midis[index] ?? event.midi
    /** Bowie: after a key change the notes are spelled in the new key */
    const name = noteLabel(to ? spellInKey(midi, to) : event.name, midi)
    return { name, label: `${name} in bar ${event.bar}` }
  })
  return {
    kind: 'ready',
    target: {
      singable,
      title: phraseTitle(source, phrase),
      reference: fit.midis,
      notes,
      audition: moved(phrase.slice, semitones, phrase.rate),
      keyLine,
      slowedLine: phrase.rate < 1 ? `Played at ${phrase.rate}× so every note is long enough to hear.` : null,
      keyChange: fit.kind === 'key-change' ? fit.keyChange : 0,
      ...((to ?? key) ? { spellingKey: to ?? key } : {}),
      ...(fit.kind === 'key-change' && fit.from && fit.to
        ? { songKey: keyDisplayName(fit.from), practiceKey: keyDisplayName(fit.to) }
        : {}),
    },
  }
}

/** A chord line is built inside the range, so nothing is moved and nothing is refused (Joni §3) */
const preparePart = (singable: Extract<Singable, { kind: 'guide-tones' | 'outline' }>): PreparedTarget => {
  const { part, source } = singable
  const kindName = part.kind === 'guide-tones' ? 'Guide tones' : 'Chord outline'
  return {
    kind: 'ready',
    target: {
      singable,
      title: `${kindName} for ${source.section}`,
      subLabel: subLabels[part.kind],
      reference: part.line.tones.map(tone => tone.midi),
      notes: part.line.tones.map((tone, index) => {
        const name = noteLabel(tone.name, tone.midi)
        /** Every chord a held common tone spans, in order (Bowie, #2440); `chord` stays the first, for spelling */
        const chords = (tone.spans ?? [tone]).map(span => displayAccidentals(span.chord)).join('–')
        return { name, label: `Note ${index + 1} (on ${chords})`, chord: tone.chord }
      }),
      audition: part.line.melody,
      keyLine: null,
      slowedLine: null,
      keyChange: 0,
    },
  }
}

export const prepareTarget = (item: PickerItem | Singable, range: VocalLib.VocalRange | null): PreparedTarget =>
  item.kind === 'phrase'
    ? preparePhrase(item.source, item.phrase, range)
    : preparePart({ kind: item.kind, source: item.source, part: item.part })
